import { addDays, format } from 'date-fns';
import { ARCHIVED_OPTIONS } from '@/constants/reportFilterConstants';
import type {
  LegacyReportFilter,
  LegacyReportPeriod,
  ReportFilterState,
  ReportOption,
  ReportPeriod,
  ReportQuery,
  ReportQueryDialect,
  ReportShowFilters,
  ReportUser,
} from '@/models/reportFilterTypes';

const DEFAULT_ARCHIVED = ARCHIVED_OPTIONS[1];

/**
 * The one place a report query is assembled.
 *
 * Replaces `reportsService.queryFromFilter` (standard reports) and
 * `reportFilterService.queryFromFilter` (generic engine). Those two had to agree on the
 * wire shape for all 68 reports and didn't. See `docs/REPORT_REPLATFORMING_TRIAGE.md`.
 *
 * Most of their divergence collapsed once normalised, because axios omits both `null`
 * and `undefined` params. Only three differences actually reach the wire, and `dialect`
 * preserves them so reports can move over one at a time:
 *
 *   1. `scopeAccount` — standard sends `''` for "no user"; generic omits it.
 *   2. `startDate`    — standard sends `''` for "no start"; generic omits it.
 *   3. `billedState` / `status` — standard defaults to `'all'`; generic omits them.
 *
 * Plus `billed` and `data`, which only the standard reports have ever sent.
 *
 * `dialect` is temporary and goes away with the last standard report.
 */

/** Both `{ id }` objects and bare ids reach these fields; legacy code relied on that. */
function idOf(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'object') {
    const { id } = value as { id?: unknown };
    return id === undefined || id === null ? undefined : String(id);
  }
  return String(value);
}

/**
 * The standard reports flatten the selected period onto itself and delete the nested
 * `range` (`commonFilter.js` `sendFilterUpdate`); the generic engine keeps it nested.
 * Accepting both is a real simplification, not a dialect difference.
 */
function rangeOf(period: ReportPeriod | null | undefined) {
  const nested = period?.range;
  const flat = period as unknown as { startDate?: Date | null; endDate?: Date | null } | undefined;
  return {
    startDate: nested?.startDate ?? flat?.startDate ?? null,
    endDate: nested?.endDate ?? flat?.endDate ?? null,
  };
}

/**
 * `YYYY-MM-DD` in local time — the same output as `Date.prototype.toNetworkDate` in
 * `services/app.extensions.js`, expressed here so this module stays a pure function
 * and doesn't depend on that file's prototype patching having run.
 */
function toNetworkDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * The API treats `endDate` as exclusive, so every call site added a day by hand. Doing
 * it here once removes the chance of a report being off by one.
 */
function networkEndDate(endDate: Date | string): string {
  return toNetworkDate(addDays(new Date(endDate), 1));
}

/**
 * Mirrors `filterService.scopeFromFilter`: with no account chosen the report is
 * firm-wide; with one chosen but no role boxes ticked it means "involved at all".
 *
 * Computed here rather than read off precomputed state. `commonFilter.js` assigned
 * `filter.filter.scope` *after* copying the payload it broadcast, so the first change
 * shipped the previous cycle's scope and relied on the deep watcher firing again to
 * converge. Deriving it removes that redundant cycle.
 */
export function scopeFromFilter(
  state: Pick<ReportFilterState, 'scopeAccount' | 'primary' | 'originating' | 'reviewer'>,
): string {
  if (!state.scopeAccount) return 'all';
  const scopes: string[] = [];
  if (state.primary) scopes.push('primary');
  if (state.reviewer) scopes.push('reviewer');
  if (state.originating) scopes.push('originating');
  return scopes.length ? scopes.join(',') : 'involved';
}

export function buildReportQuery(
  state: ReportFilterState,
  showFilters?: ReportShowFilters,
  dialect: ReportQueryDialect = 'generic',
): ReportQuery {
  const standard = dialect === 'standard';
  // An absent descriptor means "every capability on", which is what both families did.
  const wants = (key: keyof ReportShowFilters) => !showFilters || showFilters[key] === true;

  const { startDate, endDate } = rangeOf(state.dateRange);
  // A chosen distribution defines its own period, so the date range must not narrow it.
  const suppressDates = Boolean(state.distributionId);

  const query: ReportQuery = {
    source: 'reports',
    account: idOf(state.account),
    billedState: idOf(state.billed) ?? (standard ? 'all' : undefined),
    status: idOf(state.status) ?? (standard ? 'all' : undefined),
    matterTag: idOf(state.matterTag),
    distributionStatus: idOf(state.distributionStatus),
    distributionId: state.distributionId ?? undefined,
  };

  if (standard) {
    query.billed = idOf(state.billedValue) ?? 'all';
    query.data = state.data;
  }

  if (wants('matterForUser')) {
    query.scopeAccount = idOf(state.scopeAccount) ?? (standard ? '' : undefined);
    query.scope = scopeFromFilter(state);
  }

  if (wants('dateRange')) {
    query.startDate = suppressDates || !startDate
      ? (standard ? '' : undefined)
      : toNetworkDate(new Date(startDate));
    query.endDate = suppressDates || !endDate ? undefined : networkEndDate(endDate);
  }

  return query;
}

/**
 * Adapter for the standard reports' `$scope.filter`, whose shape predates this store:
 * a nested `filter` object, `billedFilter` rather than `billed`, `archived` carrying a
 * `status` id, and `scope` precomputed by the directive.
 *
 * Delete this with the last standard report.
 */
export function legacyFilterToState(
  legacy: Record<string, any> | null | undefined,
): ReportFilterState {
  const inner = legacy?.filter ?? {};
  return {
    dateRange: inner.range ?? null,
    billed: (legacy?.billedFilter ?? null) as ReportOption | null,
    contingencyBilled: (legacy?.contingencyBilledFilter ?? null) as ReportOption | null,
    // Consumers assign `filter.status = filter.archived.status` — a bare id, not an option.
    status: (legacy?.status ?? legacy?.archived?.status ?? null) as ReportOption | null,
    matterTag: legacy?.matterTag ?? null,
    trustAccount: legacy?.trustAccount ?? null,
    account: (legacy?.account ?? null) as ReportUser | string | null,
    scopeAccount: inner.scopeAccount ?? null,
    primary: Boolean(inner.primary),
    originating: Boolean(inner.originating),
    reviewer: Boolean(inner.reviewer),
    distributionId: legacy?.distributionId ?? null,
    distributionStatus: legacy?.distributionStatus ?? null,
    showEntries: legacy?.showEntries ?? true,
    showCharges: legacy?.showCharges ?? true,
    showExpenses: legacy?.showExpenses ?? true,
    showExpense: Boolean(legacy?.showExpense),
    includeBilled: Boolean(legacy?.includeBilled),
    originationUser: Boolean(legacy?.originationUser),
    // `my-productivity` is the only report that presets this before the query is built.
    billedValue: legacy?.billed ?? null,
    data: legacy?.data,
  };
}

function legacyPeriod(period: ReportPeriod | null | undefined): LegacyReportPeriod | null {
  if (!period) return null;
  const { startDate, endDate } = rangeOf(period);
  return {
    id: period.id,
    label: period.label,
    custom: period.custom,
    startDate: startDate ? new Date(startDate) : null,
    endDate: endDate ? new Date(endDate) : null,
  };
}

/**
 * The inverse of `legacyFilterToState`: the payload `commonFilter` broadcasts to the
 * standard report controllers. Six of them send `filter.range.startDate` and
 * `filter.scope` on the wire themselves (`reportsService.getClientReport`), so the
 * period is flattened and the scope precomputed. `revenueReport` hides billed totals
 * once a distribution is chosen, as `commonFilter`'s `distributionChange` did.
 *
 * Delete this with the last standard report.
 */
export function stateToLegacyFilter(
  state: ReportFilterState,
  revenueReport = false,
): LegacyReportFilter {
  const range = legacyPeriod(state.dateRange);
  // About ten controllers read `archived.status` with no null check, so never send null.
  const status = idOf(state.status) ?? DEFAULT_ARCHIVED.id;
  return {
    billedFilter: state.billed,
    contingencyBilledFilter: state.contingencyBilled,
    archived: {
      label: ARCHIVED_OPTIONS.find((o) => o.id === status)?.label ?? status,
      status,
    },
    filter: {
      range,
      scopeAccount: state.scopeAccount,
      primary: state.primary,
      originating: state.originating,
      reviewer: state.reviewer,
      scope: scopeFromFilter(state),
    },
    range,
    matterTag: { id: idOf(state.matterTag) },
    show: {
      billed: !revenueReport || (!state.distributionId && !state.distributionStatus),
      collected: true,
    },
    distributionId: state.distributionId ?? undefined,
    distributionStatus: state.distributionStatus ?? undefined,
    showExpense: state.showExpense,
  };
}

/**
 * The filters a report can actually see: anything whose control `show` hides falls back
 * to `defaults`, so a value picked on one report can't silently narrow another. No
 * descriptor means every control is shown.
 *
 * A hidden Time Period falls back to the default period, not to "no dates": several
 * reports hide it yet still send dates, and the default is what they always got for a
 * user who never changed the period, so their figures don't move.
 */
export function visibleFilterState(
  state: ReportFilterState,
  show: ReportShowFilters | undefined,
  defaults: ReportFilterState,
): ReportFilterState {
  if (!show) return state;
  const visible: ReportFilterState = { ...state };
  const reset = (
    shown: boolean | undefined,
    ...keys: Array<keyof ReportFilterState>
  ) => {
    if (shown) return;
    keys.forEach((key) => {
      (visible as unknown as Record<string, unknown>)[key] = defaults[key];
    });
  };
  reset(show.dateRange, 'dateRange');
  reset(
    show.matterForUser,
    'scopeAccount',
    'primary',
    'originating',
    'reviewer',
  );
  reset(show.archivedStatus || show.matterForUser, 'status');
  reset(show.matterTags, 'matterTag');
  reset(show.billed, 'billed');
  reset(show.contingencyBilled, 'contingencyBilled');
  reset(show.distribution, 'distributionId', 'distributionStatus');
  reset(show.trustAccount, 'trustAccount');
  reset(show.itemType, 'showEntries', 'showCharges', 'showExpenses');
  reset(show.includeBilled, 'includeBilled');
  reset(show.originationUser, 'originationUser');
  reset(show.showExpense, 'showExpense');
  return visible;
}
